import { Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/lib/auth-context";
import { RequireAuth, RequirePlatformAdmin } from "@/lib/route-guards";

import { Home } from "@/routes/public/Home";
import { Login } from "@/routes/public/Login";
import { Signup } from "@/routes/public/Signup";

import { DashboardLayout } from "@/routes/customer/DashboardLayout";
import { DashboardHome } from "@/routes/customer/DashboardHome";
import { Leads } from "@/routes/customer/Leads";
import { LeadDetail } from "@/routes/customer/LeadDetail";
import { Conversations } from "@/routes/customer/Conversations";
import { ConversationDetail } from "@/routes/customer/ConversationDetail";
import { Calls } from "@/routes/customer/Calls";
import { Analytics } from "@/routes/customer/Analytics";
import { Automation } from "@/routes/customer/Automation";
import { Settings } from "@/routes/customer/Settings";
import { Billing } from "@/routes/customer/Billing";

import { AdminLayout } from "@/routes/admin/AdminLayout";
import { AdminHome } from "@/routes/admin/AdminHome";
import { Customers } from "@/routes/admin/Customers";
import { CustomerDetail } from "@/routes/admin/CustomerDetail";
import { Finance } from "@/routes/admin/Finance";
import { AdminAnalytics } from "@/routes/admin/AdminAnalytics";

/**
 * `/dashboard/*` is wrapped in <RequireAuth>, `/admin/*` in
 * <RequirePlatformAdmin> (see src/lib/route-guards.tsx). Both are a UX
 * convenience only: the real security boundary is the backend's
 * requireAuth/requireOrganizationMember/requirePlatformAdmin middleware
 * (apps/api/src/middleware/), since a frontend guard can always be
 * bypassed by calling the API directly.
 */
export default function App() {
  return (
    <AuthProvider>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />

        {/* Customer dashboard */}
        <Route
          path="/dashboard"
          element={
            <RequireAuth>
              <DashboardLayout />
            </RequireAuth>
          }
        >
          <Route index element={<DashboardHome />} />
          <Route path="leads" element={<Leads />} />
          <Route path="leads/:id" element={<LeadDetail />} />
          <Route path="conversations" element={<Conversations />} />
          <Route path="conversations/:id" element={<ConversationDetail />} />
          <Route path="calls" element={<Calls />} />
          <Route path="analytics" element={<Analytics />} />
          <Route path="automation" element={<Automation />} />
          <Route path="billing" element={<Billing />} />
          <Route path="settings" element={<Settings />} />
        </Route>

        {/* Platform admin */}
        <Route
          path="/admin"
          element={
            <RequirePlatformAdmin>
              <AdminLayout />
            </RequirePlatformAdmin>
          }
        >
          <Route index element={<AdminHome />} />
          <Route path="customers" element={<Customers />} />
          <Route path="customers/:id" element={<CustomerDetail />} />
          <Route path="finance" element={<Finance />} />
          <Route path="analytics" element={<AdminAnalytics />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AuthProvider>
  );
}

function NotFound() {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-2 bg-paper text-center">
      <p className="font-display text-3xl text-ink">Page not found</p>
      <a href="/" className="text-sm text-brand-700 hover:underline">
        Back to home
      </a>
    </div>
  );
}
